import React, { Component, ClassAttributes } from 'react';
import { Decimal } from 'decimal.js';
import classnames from 'classnames';
import './styles.css';

interface IWeightsState {
  [key: string]: null | {
    anchors: {
      [key: string]: Decimal;
    };
    units: {
      [key: string]: IWeightsUnit;
    };
  };
}

interface IWeightsUnit {
  name: string;
  symbol: string;
  factor: Decimal;
  value: Decimal;
  raw: string;
  error: boolean;
}

export default class Weights extends Component<{}, IWeightsState> {
  KEY = 'Weights';

  constructor(props: ClassAttributes<{}>) {
    super(props);

    this.keyUpHandler = this.keyUpHandler.bind(this);

    this.state = {
      metric: null,
      imperial: null
    }

    let stateFromStorage: string | null = localStorage.getItem(this.KEY);
    if (stateFromStorage) {
      this.state = JSON.parse(stateFromStorage);

      Object.entries(this.state).forEach(([groupKey, group]) => {
        Object.entries(group!.anchors).forEach(([anchorKey, anchor]) => {
          this.state[groupKey]!.anchors[anchorKey] = new Decimal(anchor);
        });

        Object.entries(group!.units).forEach(([unitKey, unit]) => {
          this.state[groupKey]!.units[unitKey].value = new Decimal(unit.value);
          this.state[groupKey]!.units[unitKey].factor = new Decimal(unit.factor);
        });
      });
    }
  }

  componentDidMount() {
    const { metric, imperial } = this.state;

    if (!metric || !imperial) {
      this.reset();
    }
  }

  reset() {
    this.setState({
      metric: {
        anchors: {
          imperial: new Decimal('0.03527396')
        },
        units: {
          nanogram: {
            name: 'nanogram',
            symbol: 'ng',
            factor: new Decimal('0.000000001'),
            value: new Decimal(0),
            raw: '',
            error: false
          },
          microgram: {
            name: 'microgram',
            symbol: 'µg',
            factor: new Decimal('0.000001'),
            value: new Decimal(0),
            raw: '',
            error: false
          },
          milligram: {
            name: 'milligram',
            symbol: 'mg',
            factor: new Decimal('0.001'),
            value: new Decimal(0),
            raw: '',
            error: false
          },
          gram: {
            name: 'gram',
            symbol: 'g',
            factor: new Decimal(1),
            value: new Decimal(0),
            raw: '',
            error: false
          },
          kilogram: {
            name: 'kilogram',
            symbol: 'kg',
            factor: new Decimal(1000),
            value: new Decimal(0),
            raw: '',
            error: false
          },
          tonne: {
            name: 'megagram (tonne)',
            symbol: 'Mg',
            factor: new Decimal(1000000),
            value: new Decimal(0),
            raw: '',
            error: false
          }
        }
      },
      imperial: {
        anchors: {
          metric: new Decimal('28.34952')
        },
        units: {
          grain: {
            name: 'grain',
            symbol: 'gr',
            factor: new Decimal('0.00228571428571428571428571428571'),
            value: new Decimal(0),
            raw: '',
            error: false
          },
          drachm: {
            name: 'drachm',
            symbol: 'dr',
            factor: new Decimal('0.0625'),
            value: new Decimal(0),
            raw: '',
            error: false
          },
          ounce: {
            name: 'ounce',
            symbol: 'oz',
            factor: new Decimal(1),
            value: new Decimal(0),
            raw: '',
            error: false
          },
          pound: {
            name: 'pound',
            symbol: 'lb',
            factor: new Decimal(16),
            value: new Decimal(0),
            raw: '',
            error: false
          },
          stone: {
            name: 'stone',
            symbol: 'st',
            factor: new Decimal(224),
            value: new Decimal(0),
            raw: '',
            error: false
          },
          quarter: {
            name: 'quarter',
            symbol: 'qtr',
            factor: new Decimal('448'),
            value: new Decimal(0),
            raw: '',
            error: false
          },
          shundredweight: {
            name: 'short hundredweight',
            symbol: 'cwt',
            factor: new Decimal(1600),
            value: new Decimal(0),
            raw: '',
            error: false
          },
          lhundredweight: {
            name: 'long hundredweight',
            symbol: 'cwt',
            factor: new Decimal(1792),
            value: new Decimal(0),
            raw: '',
            error: false
          },
          ton: {
            name: 'ton',
            symbol: 't',
            factor: new Decimal(32000),
            value: new Decimal(0),
            raw: '',
            error: false
          }
        }
      }
    });
  }

  keyUpHandler(groupName: string, unitName: string, event: any) {
    event.preventDefault();

    const metric = Object.assign({}, this.state.metric);
    const imperial = Object.assign({}, this.state.imperial);
    let raw: any = event.target.value;

    // Set the raw values
    if (groupName === 'metric') {
      metric.units[unitName].raw = raw;
    } else if (groupName === 'imperial') {
      imperial.units[unitName].raw = raw;
    }

    try {
      const newValue = new Decimal(raw);

      // Set the base values
      if (groupName === 'metric') {
        metric.units[unitName].value = newValue;
        metric.units[unitName].raw = raw;
        metric.units[unitName].error = false;

        if (unitName !== 'gram') {
          metric.units['gram'].value = newValue.times(metric.units[unitName].factor);
          metric.units['gram'].raw = metric.units['gram'].value.toFixed();
          metric.units['gram'].error = false;
        }
      } else if (groupName === 'imperial') {
        imperial.units[unitName].value = newValue;
        imperial.units[unitName].raw = raw;
        imperial.units[unitName].error = false;

        if (unitName !== 'ounce') {
          imperial.units['ounce'].value = newValue.times(imperial.units[unitName].factor);
          imperial.units['ounce'].raw = imperial.units['ounce'].value.toFixed();
          imperial.units['ounce'].error = false;
        }
      }

      // Update other base values
      if (groupName === 'metric') {
        imperial.units['ounce'].value = newValue.times(metric.anchors.imperial);
        imperial.units['ounce'].raw = imperial.units['ounce'].value.toFixed();
        imperial.units['ounce'].error = false;
      } else if (groupName === 'imperial') {
        metric.units['gram'].value = newValue.times(imperial.anchors.metric);
        metric.units['gram'].raw = metric.units['gram'].value.toFixed();
        metric.units['gram'].error = false;
      }

      // Update non base values
      Object.entries(metric.units).forEach(([key]) => {
        if (key === 'gram' || key === unitName) {
          return;
        }

        metric.units[key].value = metric.units['gram'].value.dividedBy(metric.units[key].factor);
        metric.units[key].raw = metric.units[key].value.toFixed();
        metric.units[key].error = false;
      });

      Object.entries(imperial.units).forEach(([key]) => {
        if (key === 'ounce' || key === unitName) {
          return;
        }

        imperial.units[key].value = imperial.units['ounce'].value.dividedBy(imperial.units[key].factor);
        imperial.units[key].raw = imperial.units[key].value.toFixed();
        imperial.units[key].error = false;
      });
    } catch (e) {
      // Set the raw values
      if (groupName === 'metric') {
        metric.units[unitName].error = true;
      } else if (groupName === 'imperial') {
        imperial.units[unitName].error = true;
      }
    }

    this.setState({ metric, imperial });
  }

  render() {
    localStorage.setItem(this.KEY, JSON.stringify(this.state));

    const { metric, imperial } = this.state;

    if (!metric || !imperial) {
      return null;
    }

    return (
      <div className="tool container-fluid">
        <div className="tool-header row">
          <div className="tool-title col-md-6">
            <h1 className="tool-name">Weights</h1>
          </div>
          <div className="tool-buttons col-md-6 text-right">
            <button className="btn btn-danger" onClick={this.reset.bind(this)}>Reset</button>
          </div>
        </div>
        <div className="row tool-body">
          <div className="col-md-12">
            <table className="group-metric table border-bottom">
              <thead className="thead-light">
                <tr>
                  <th className="text-center" colSpan={3}>Metric</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(metric.units).map(([key, unit]) => {
                  return (
                    <tr key={'metric_' + key}>
                      <td className="align-middle unit-name">
                        <strong>{unit.name}</strong>
                        <span className="unit-symbol">{Boolean(unit.symbol) && <em> ({unit.symbol})</em>}</span>
                      </td>
                      <td>
                        <input
                          type="text"
                          className={classnames({
                            'form-control': true,
                            'is-invalid': unit.error
                          })}
                          placeholder="0"
                          value={unit.raw === '' ? '' : '' + unit.raw}
                          onChange={(event) => this.keyUpHandler('metric', key, event)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <table className="group-imperial table border-bottom">
              <thead className="thead-light">
                <tr>
                  <th className="text-center" colSpan={3}>Imperial</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(imperial.units).map(([key, unit]) => {
                  return (
                    <tr key={'imperial_' + key}>
                      <td className="align-middle unit-name">
                        <strong>{unit.name}</strong>
                        <span className="unit-symbol">{Boolean(unit.symbol) && <em> ({unit.symbol})</em>}</span>
                      </td>
                      <td>
                        <input
                          type="text"
                          className={classnames({
                            'form-control': true,
                            'is-invalid': unit.error
                          })}
                          placeholder="0"
                          value={unit.raw === '' ? '' : '' + unit.raw}
                          onChange={(event) => this.keyUpHandler('imperial', key, event)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }
}